import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Eye, Mail, Phone, MapPin, Truck, MoreHorizontal, DollarSign, Flame, ThermometerSun, Snowflake, MessageCircle, ClipboardList, Package } from 'lucide-react';
import { Button } from '../ui/button';
import { formatUsPhone } from '../../utils/phone';
import { getAuthToken } from '../../utils/auth';
import DailyMatchEngagement from './DailyMatchEngagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_COLUMNS = [
  { id: 'facebook', label: 'Facebook', color: 'sky', bgLight: 'bg-sky-50', bgDark: 'bg-sky-900/20', borderColor: 'border-sky-200', dotColor: 'bg-[#1877F2]' },
  { id: 'new', label: 'New', color: 'blue', bgLight: 'bg-blue-50', bgDark: 'bg-blue-900/20', borderColor: 'border-blue-200', dotColor: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'amber', bgLight: 'bg-amber-50', bgDark: 'bg-amber-900/20', borderColor: 'border-amber-200', dotColor: 'bg-amber-500' },
  { id: 'qualified', label: 'Qualified', color: 'emerald', bgLight: 'bg-emerald-50', bgDark: 'bg-emerald-900/20', borderColor: 'border-emerald-200', dotColor: 'bg-emerald-500' },
  { id: 'closed', label: 'Sold', color: 'green', bgLight: 'bg-green-50', bgDark: 'bg-green-900/20', borderColor: 'border-green-200', dotColor: 'bg-green-600' },
  { id: 'bad_lead', label: 'Lost', color: 'slate', bgLight: 'bg-slate-100', bgDark: 'bg-slate-800/50', borderColor: 'border-slate-300', dotColor: 'bg-slate-400' },
];

// A lead that arrived from Facebook/Instagram ads or referrals, or one the
// advisor manually dragged into the Facebook column (fb_column override).
const isFacebookLead = (lead) => {
  if (lead?.fb_column === true) return true;
  if (lead?.fb_column === false) return false;
  if (lead?.fbclid || lead?.attribution?.fbclid) return true;
  const src = [lead?.source, lead?.utm_source, lead?.attribution?.utm_source, lead?.original_source]
    .filter(Boolean).join(' ').toLowerCase();
  const ref = String(lead?.attribution?.referrer || lead?.referrer || '').toLowerCase();
  return /facebook|instagram|\bfb\b|\bmeta\b/.test(src) || ref.includes('facebook.') || ref.includes('instagram.') || ref.includes('fb.com');
};

const KanbanPipeline = ({ leads, onLeadClick, onStatusChange, onConvertToSale, isDark }) => {
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  // Map { lead_id -> { count, top_item } } from a single batch endpoint.
  // Powers the "N fits" chip on each card without an N+1 request storm.
  const [inventoryMatches, setInventoryMatches] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const r = await axios.get(`${API}/dealer/local-matches-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInventoryMatches(r.data?.summary || {});
      } catch {
        setInventoryMatches({});
      }
    })();
  }, []);

  // Group leads by status. "Facebook" is a source column: it holds untouched
  // (new) Facebook leads; once contacted they flow through the normal stages.
  const getLeadsByStatus = (status) => {
    // Map some statuses for column grouping
    const statusMap = {
      'added_to_crm': 'contacted',
      'unqualified': 'bad_lead',
    };
    return leads.filter(lead => {
      const mappedStatus = statusMap[lead.status] || lead.status;
      if (status === 'facebook') return mappedStatus === 'new' && isFacebookLead(lead);
      if (status === 'new') return mappedStatus === 'new' && !isFacebookLead(lead);
      return mappedStatus === status;
    });
  };

  const handleDragStart = (result) => {
    setDraggedLeadId(result.draggableId);
  };

  const handleDragEnd = (result) => {
    setDraggedLeadId(null);
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    
    // Facebook column = new-stage leads tagged Facebook. Dropping in tags the
    // lead (sticks); dragging out to New untags it so it stays in New.
    if (destination.droppableId === 'facebook') {
      onStatusChange(draggableId, 'new', { fb_column: true });
    } else if (source.droppableId === 'facebook' && destination.droppableId === 'new') {
      onStatusChange(draggableId, 'new', { fb_column: false });
    } else {
      onStatusChange(draggableId, destination.droppableId);
    }
  };

  return (
    <div className="h-full" data-testid="kanban-pipeline">
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 h-full overflow-x-auto pb-4 px-1">
          {PIPELINE_COLUMNS.map((column) => {
            const columnLeads = getLeadsByStatus(column.id);
            return (
              <div 
                key={column.id} 
                className="flex-shrink-0 w-72"
                data-testid={`kanban-column-${column.id}`}
              >
                {/* Column Header */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-t-xl ${isDark ? column.bgDark : column.bgLight}`}>
                  <div className={`w-2.5 h-2.5 rounded-full ${column.dotColor}`} />
                  <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-700'}`}>
                    {column.label}
                  </span>
                  <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                    isDark ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-600'
                  }`}>
                    {columnLeads.length}
                  </span>
                </div>

                {/* Drop Zone */}
                <Droppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[calc(100vh-280px)] p-2 rounded-b-xl transition-colors duration-200 ${
                        snapshot.isDraggingOver 
                          ? isDark ? 'bg-blue-900/30 ring-2 ring-blue-500/50' : 'bg-blue-50 ring-2 ring-blue-300'
                          : isDark ? 'bg-slate-800/50' : 'bg-slate-50'
                      }`}
                    >
                      {columnLeads.map((lead, index) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`mb-2 ${snapshot.isDragging ? 'rotate-2' : ''}`}
                            >
                              <LeadCard 
                                lead={lead} 
                                isDark={isDark} 
                                isDragging={snapshot.isDragging}
                                onViewClick={() => onLeadClick(lead)}
                                onConvertClick={() => onConvertToSale(lead)}
                                inventoryMatch={inventoryMatches[lead.id]}
                                showConvert={
                                  column.id === 'qualified' ||
                                  column.id === 'closed' ||
                                  (lead.engagement?.level === 'hot') ||
                                  (typeof lead.intent_score === 'number' && lead.intent_score >= 80)
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {columnLeads.length === 0 && !snapshot.isDraggingOver && (
                        <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          <p className="text-sm">No leads</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
};

const LeadSourceBadges = ({ lead }) => {
  const hasQuiz = lead?.has_quiz ?? Boolean(lead?.user_responses && Object.keys(lead.user_responses).length > 0);
  const hasRanger = Boolean(lead?.has_ranger_chat);
  if (!hasQuiz && !hasRanger) return null;
  const both = hasQuiz && hasRanger;
  return (
    <div className="flex items-center gap-1 mt-1 flex-wrap" data-testid={`lead-source-badges-${lead.id}`}>
      {both && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-blue-500 to-orange-500 text-white shadow-sm"
          title="Completed the Form AND a Ranger chat"
        >
          ★ F + R
        </span>
      )}
      {!both && hasQuiz && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-600 border border-blue-500/30"
          title="Completed the Form"
        >
          <ClipboardList size={9} /> Form
        </span>
      )}
      {!both && hasRanger && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-bold uppercase tracking-wider bg-orange-500/15 text-orange-600 border border-orange-500/30"
          title="Saved a Ranger chat"
        >
          <MessageCircle size={9} /> Ranger
        </span>
      )}
    </div>
  );
};

const LeadCard = ({ lead, isDark, isDragging, onViewClick, onConvertClick, showConvert, inventoryMatch }) => {
  const intentColor = lead.intent_score >= 80 ? 'text-emerald-500' :
                     lead.intent_score >= 60 ? 'text-amber-500' :
                     lead.intent_score >= 40 ? 'text-orange-500' : 'text-red-500';

  // Engagement level styling
  const engagementConfig = {
    hot: { 
      icon: Flame, 
      color: 'text-red-500', 
      bg: 'bg-red-100 dark:bg-red-900/30', 
      label: 'Hot',
      border: 'border-red-200'
    },
    warm: { 
      icon: ThermometerSun, 
      color: 'text-amber-500', 
      bg: 'bg-amber-100 dark:bg-amber-900/30', 
      label: 'Warm',
      border: 'border-amber-200'
    },
    cold: { 
      icon: Snowflake, 
      color: 'text-blue-400', 
      bg: 'bg-blue-100 dark:bg-blue-900/30', 
      label: 'Cold',
      border: 'border-blue-200'
    }
  };
  
  const engagement = lead.engagement || { level: 'warm', score: 50 };
  const engagementStyle = engagementConfig[engagement.level] || engagementConfig.warm;
  const EngagementIcon = engagementStyle.icon;

  return (
    <div 
      className={`rounded-xl p-3 transition-all duration-200 cursor-grab active:cursor-grabbing
        ${isDragging 
          ? 'shadow-2xl scale-105 ring-2 ring-blue-500' 
          : 'shadow-sm hover:shadow-md'
        }
        ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Engagement Badge */}
      <div className="flex items-center justify-between mb-2">
        <div 
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${engagementStyle.bg} ${engagementStyle.color}`}
          title={`Engagement Score: ${engagement.score}/100${engagement.factors?.length ? '\n' + engagement.factors.join(', ') : ''}`}
          data-testid={`engagement-badge-${lead.id}`}
        >
          <EngagementIcon size={12} />
          {engagementStyle.label}
        </div>
        <div className={`text-lg font-bold ${intentColor}`} title="Intent Score">
          {lead.intent_score}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {lead.name || 'Pending'}
          </h4>
          <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {lead.email}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            {isFacebookLead(lead) && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-[1px] mt-1 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#1877F2]/15 text-[#1877F2] border border-[#1877F2]/30"
                title="Came from Facebook / Instagram"
                data-testid={`fb-badge-${lead.id}`}
              >
                f Facebook
              </span>
            )}
          </div>
          <LeadSourceBadges lead={lead} />
          {lead.email && (
            <div className="mt-1.5">
              <DailyMatchEngagement email={lead.email} compact />
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-3">
        {lead.phone && (
          <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Phone size={12} />
            <span>{formatUsPhone(lead.phone)}</span>
          </div>
        )}
        {lead.zipcode && (
          <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <MapPin size={12} />
            <span>{lead.zipcode}</span>
          </div>
        )}
        {lead.rv_type && (
          <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <Truck size={12} />
            <span className="font-medium">{lead.rv_type}</span>
          </div>
        )}
      </div>

      {/* RV Matches Badge */}
      {lead.matched_rvs && lead.matched_rvs.length > 0 && (
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mb-3
          ${isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
          <Truck size={12} />
          {lead.matched_rvs.length} Matches
        </div>
      )}

      {/* Local inventory chip — dealer's uploaded inventory match count.
          Shows a compact pill like "2 fits: 2024 Salem 263BHXL" so
          sellers instantly see when they own a matching unit. */}
      {inventoryMatch?.count > 0 && (
        <div
          className={`inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-full text-xs font-medium mb-3 ml-1
            ${isDark ? 'bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/25' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200'}`}
          data-testid={`kanban-inv-chip-${lead.id}`}
          title={`${inventoryMatch.count} match${inventoryMatch.count === 1 ? '' : 'es'} from your uploaded inventory`}
        >
          <Package size={12} className="shrink-0" />
          <span className="truncate">
            {inventoryMatch.count} fit{inventoryMatch.count === 1 ? '' : 's'}
            {inventoryMatch.top_item?.model ? (
              <>: {[inventoryMatch.top_item.year, inventoryMatch.top_item.make, inventoryMatch.top_item.model].filter(Boolean).join(' ')}</>
            ) : null}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onViewClick(); }}
          className={`flex-1 h-8 text-xs font-semibold ${
            isDark
              ? 'bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white'
              : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
          }`}
          data-testid={`view-lead-${lead.id}`}
        >
          <Eye size={14} className="mr-1" />
          View
        </Button>
        {showConvert && (
          <Button 
            size="sm"
            onClick={(e) => { e.stopPropagation(); onConvertClick(); }}
            className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
            data-testid={`convert-lead-${lead.id}`}
          >
            <DollarSign size={14} className="mr-1" />
            Sale
          </Button>
        )}
      </div>
    </div>
  );
};

export default KanbanPipeline;
